import React, { useEffect, useMemo, useState } from "react";
import axios from "api/axios";
import { Button, IconButton } from "components/button";
import { Input } from "components/input";
import EmailInput from "components/input/EmailInput";
import PhoneNumberInput from "components/input/phoneNumberInput";
import { StyledLabel } from "components/input/style";
import SafeArea from "components/safe-area";
import { useSnackbarContext } from "components/snackbar";
import Spinner from "components/spinner";
import { useFormik } from "formik";
import { cloneDeep } from "lodash";
import { useMutation, useQuery } from "react-query";
import { useHistory, useParams } from "react-router-dom";
import { CustomerEdit, TCustomer } from "types/customer";
import { TProperty } from "types/property";
import { TTag } from "types/tag";
import * as Yup from "yup";
import TagManagement from "./tags";
import TagManager from "./TagManager";
import { useTranslation } from "react-i18next";
import ContactFields from "./components/ContactFields";
import { Dropdown } from "./components/Dropdown";
import FavoriteStarButton from "./components/FavoriteStarButton";
import {
  applyPrimaryKeysToContactInfo,
  deriveDefaultPrimaryKeysFromContactInfos,
  extendedToContactInfosForAdditional,
  extendedToContactInfosForPrimary,
  normalizeContactInfoForApi,
  parsePrimaryDefaultExtraIndex,
} from "utils/customerContactInfo";
import {
  customerFormEmailsAreValid,
  isValidEmail,
} from "utils/emailValidation";
import { formatPhoneNumber } from "utils/phoneNumberFormatter";

export default function NewCustomer() {
  const { t } = useTranslation();
  const tBase = "views.customers.new-customer.index";
  const tr = (key: string) => t(`${tBase}.${key}`);
  const [isQbNameEditable, setIsQbNameEditable] = useState(false);

  const [addressObj, setAddressObj] = useState<any>({
    address: "",
    isStandardizedAddress: false,
    selectedOption: {
      description: "",
      city: "",
      state: "",
      zipcode: "",
    },
  });

  const params = useParams<{ id: string }>(),
    history = useHistory(),
    { showSnackbar } = useSnackbarContext();

  const isEditing = params.id !== "new";

  const tagsQuery = useQuery<Array<TTag>>("tags", async () =>
    axios
      .get(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/tags/customer&property`)
      .then((res) => res.data)
  );

  const query = useQuery<{ info: TCustomer }>(
    ["customer", "editing", params.id],
    async () =>
      axios
        .get(
          `${process.env.REACT_APP_SERVER_URL}/api/v1/company/customers/${params.id}`
        )
        .then((res) => res.data),
    { enabled: isEditing }
  );

  const submitChanges = useMutation(
    async (e: object) =>
      await axios
        .post(
          `
    ${process.env.REACT_APP_SERVER_URL}/api/v1/company/customers/${params.id}
    `,
          {
            ...e,
            address: addressObj.address,
            isStandardizedAddress: addressObj.isStandardizedAddress,
          }
        )
        .then((res) => res.data),
    {
      onSuccess: (res) => {
        params.id === "new"
          ? showSnackbar(tr("Customer created!"))
          : showSnackbar(tr("Customer updated!"));
        history.push(`/customers/${res}`);
      },
    }
  );

  const contact = {
    is_new: true,
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    extension: "",
    title: "",
    is_billing: false,
    is_notification: false,
    contact_info: [] as CustomerEdit["primary_contact_info"],
  };

  const mapContactsFromApi = (info?: TCustomer | null) => {
    if (!info?.contacts?.length) return [];
    return info.contacts.map((c) => ({
      id: c.id,
      first_name: c.first_name || "",
      last_name: c.last_name || "",
      email: c.email || "",
      phone: c.phone || "",
      extension: c.extension || "",
      title: c.title || "",
      is_billing: c.is_billing ?? false,
      is_notification: c.is_notification ?? false,
      is_new: false,
      contact_info: extendedToContactInfosForAdditional(
        info.extended_contacts,
        c.id
      ),
    }));
  };

  const primaryListForInitial = isEditing
    ? extendedToContactInfosForPrimary(query.data?.info?.extended_contacts)
    : [];
  const defaultPrimaryKeys =
    deriveDefaultPrimaryKeysFromContactInfos(primaryListForInitial);

  const initialValues: CustomerEdit = {
    first_name: query.data?.info.first_name || "",
    last_name: query.data?.info.last_name || "",
    company_name: query.data?.info.company_name || "",
    title: query.data?.info.title || "",
    email: query.data?.info.email || "",
    phone: query.data?.info.phone || "",
    extension: query.data?.info.extension || "",
    address: "",
    city: "",
    state: "",
    name: "",
    zipcode: "",
    contacts: isEditing
      ? mapContactsFromApi(query.data?.info)
      : [],
    customer_tags: query.data?.info.customer_tags || [],
    qb_name: query.data?.info.qb_name || "",
    primary_contact_info: primaryListForInitial,
    fk_billing_address_id: isEditing
      ? query.data?.info?.fk_billing_address_id ?? null
      : null,
    is_billing: isEditing
      ? query.data?.info?.is_billing ?? true
      : true,
    is_notification: isEditing
      ? query.data?.info?.is_notification ?? true
      : true,
    defaultPrimaryEmailKey: defaultPrimaryKeys.email,
    defaultPrimaryPhoneKey: defaultPrimaryKeys.phone,
  };

  // const onSubmit = (e: any) => {
  //   submitChanges.mutate(e);
  // };

  const onSubmit = (e: CustomerEdit) => {
    const {
      defaultPrimaryEmailKey,
      defaultPrimaryPhoneKey,
      ...customerRest
    } = e;
    const contactsPayload = (e.contacts || [])
      .filter(Boolean)
      .map((c) => {
        const row: Record<string, unknown> = {
          is_new: !!c!.is_new,
          first_name: c!.first_name,
          last_name: c!.last_name,
          email: c!.email,
          phone: c!.phone,
          extension: c!.extension,
          title: c!.title,
          is_billing: !!c!.is_billing,
          is_notification: !!c!.is_notification,
          contact_info: normalizeContactInfoForApi(c!.contact_info),
        };
        if (c!.id != null && Number.isFinite(Number(c!.id))) {
          row.id = c!.id;
        }
        return row;
      });
    const trimmedData: Record<string, unknown> = {
      ...customerRest,
      email: (e.email ?? "").trim() || null,
      contacts: contactsPayload,
      primary_contact_info: normalizeContactInfoForApi(
        applyPrimaryKeysToContactInfo(
          e.primary_contact_info,
          defaultPrimaryEmailKey ?? "main",
          defaultPrimaryPhoneKey ?? "main"
        )
      ),
    };
    if (isEditing) {
      trimmedData.fk_billing_address_id = e.fk_billing_address_id ?? null;
    }
    submitChanges.mutate(trimmedData);
  };

  // addresses: Yup.array().of(
  //   Yup.object().shape({
  //     address: Yup.string().required(),
  //     city: Yup.string().required(),
  //     zipcode: Yup.string().min(5).max(5).required(),
  //     state: Yup.string().min(2).max(2).required(),
  //     name: Yup.string().nullable(),
  //   })
  // ),

  const validationSchema = useMemo(() => {
    const contactInfoShape = Yup.object().shape({
      contact_type: Yup.string().oneOf(["email", "phone"]),
      contact_value: Yup.string().when("contact_type", {
        is: "email",
        then: (schema) =>
          schema
            .nullable()
            .test("email", tr("Invalid email"), (val) => isValidEmail(val)),
        otherwise: (schema) => schema.nullable(),
      }),
      contact_description: Yup.string().max(255).nullable(),
      is_billing: Yup.boolean(),
      is_notification: Yup.boolean(),
    })

    return Yup.object({
      first_name: Yup.string().test({
        name: "first_name",
        message:
          tr("Please enter either First/Last Name or Company Name"),
        test: function (val) {
          return this.parent.company_name || val
        },
      }),
      company_name: Yup.string(),
      last_name: Yup.string().test({
        name: "first_name",
        message:
          tr("Please enter either First/Last Name or Company Name"),
        test: function (val) {
          return this.parent.company_name || val
        },
      }),
      email: Yup.string()
        .nullable()
        .test("email", tr("Invalid email"), (val) => isValidEmail(val)),
      phone: Yup.string().nullable(),
      extension: Yup.string().nullable(),
      title: Yup.string().nullable(),

      primary_contact_info: Yup.array().of(contactInfoShape),
      fk_billing_address_id: Yup.number().nullable(),
      is_billing: Yup.boolean(),
      is_notification: Yup.boolean(),
      contacts: Yup.array().of(
        Yup.object().shape({
          first_name: Yup.string().required(tr("Please enter a first name")),
          last_name: Yup.string().required(tr("Please enter a last name")),
          email: Yup.string()
            .nullable()
            .test("email", tr("Invalid email"), (val) => isValidEmail(val)),
          phone: Yup.string().nullable(),
          extension: Yup.string().nullable(),
          title: Yup.string().nullable(),
          is_billing: Yup.boolean(),
          is_notification: Yup.boolean(),
          contact_info: Yup.array().of(contactInfoShape),
        })
      ),
      name: Yup.string(),

      customer_tags: Yup.array().of(
        Yup.object().shape({
          id: Yup.string().nullable(),
          label: Yup.string(),
          color: Yup.string().nullable(),
        })
      ),

      qb_name: Yup.string().nullable(),
    })
  }, [tr])

  const formik = useFormik<CustomerEdit>({
    initialValues,
    onSubmit,
    validationSchema,
    enableReinitialize: true,
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,
  })

  const emailsValid = useMemo(
    () => customerFormEmailsAreValid(formik.values),
    [formik.values]
  )

  useEffect(() => {
    const list = formik.values.primary_contact_info || [];
    const emailKey = formik.values.defaultPrimaryEmailKey ?? "main";
    const phoneKey = formik.values.defaultPrimaryPhoneKey ?? "main";

    if (emailKey !== "main") {
      const idx = parsePrimaryDefaultExtraIndex(emailKey);
      const row = idx != null ? list[idx] : null;
      if (idx == null || !row || row.contact_type !== "email") {
        void formik.setFieldValue("defaultPrimaryEmailKey", "main");
      }
    }
    if (phoneKey !== "main") {
      const idx = parsePrimaryDefaultExtraIndex(phoneKey);
      const row = idx != null ? list[idx] : null;
      if (idx == null || !row || row.contact_type !== "phone") {
        void formik.setFieldValue("defaultPrimaryPhoneKey", "main");
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- only reset defaults when extra list changes
  }, [formik.values.primary_contact_info]);

  React.useEffect(() => {
    formik.setValues((v) => ({
      ...v,
      address: addressObj.address,
      // address: addressObj.selectedOption.description,
      city: addressObj.selectedOption.city,
      state: addressObj.selectedOption.state,
      zipcode: addressObj.selectedOption.zipcode,
    }));
  }, [addressObj]);


  const handleUpdateTag = (
    customerId: number | string,
    tagId: number | string,
    type: string
  ) => {
    formik.setValues((v: any) => {
      const tempTags = v.customer_tags;
      const newTag = tagsQuery.data?.filter((t: any) => t.id == tagId)[0];
      const filteredTags = [...tempTags.filter((t: any) => t && t.id != tagId)];

      return {
        ...v,
        customer_tags:
          type === "add" ? [...filteredTags, newTag] : [...filteredTags],
      };
    });
  };

  const updateFormikTags = (updatedTags: any) => {
    formik.setFieldValue("customer_tags", updatedTags, true);
    formik.setFieldTouched("customer_tags", true, true); // Mark the field as touched
    formik.validateForm(); // Trigger validation
  };

  const primaryEmailSelectOptions = useMemo(() => {
    const pci = formik.values.primary_contact_info || [];
    const opts = [
      {
        value: "main",
        label: `${formik.values.email} (Primary)`,
      },
    ];
    pci.forEach((row, i) => {
      if (
        row.contact_type === "email" &&
        (row.contact_value || "").toString().trim()
      ) {
        opts.push({
          value: `extra:${i}`,
          label: `${row.contact_value}${row.contact_description ? ` (${row.contact_description})` : ""}`,
        });
      }
    });
    return opts;
  }, [formik.values.primary_contact_info, formik.values.email]);

  const primaryPhoneSelectOptions = useMemo(() => {
    const pci = formik.values.primary_contact_info || [];
    const opts = [
      {
        value: "main",
        label: `${formatPhoneNumber(formik.values.phone ?? "") || "—"} (Primary)`,
      },
    ];
    pci.forEach((row, i) => {
      if (
        row.contact_type === "phone" &&
        (row.contact_value || "").toString().trim()
      ) {
        opts.push({
          value: `extra:${i}`,
          label: `${formatPhoneNumber(row.contact_value ?? "") || "—"}${row.contact_description ? ` (${row.contact_description})` : ""}`,
        });
      }
    });
    return opts;
  }, [formik.values.primary_contact_info, formik.values.phone]);

  if (isEditing && query.isLoading) return <Spinner />;

  const populateContacts = formik.values.contacts?.map((contact, i) => {
    const deleteContact = () => {
      formik.setValues((v) => {
        let tempContacts = cloneDeep(v.contacts);
        tempContacts.splice(i, 1);
        return {
          ...v,
          contacts: tempContacts,
        };
      });
    };

    if (!contact) return null;

    return (
      <div className="border-b border-black py-4 mt-4 mb-4" key={contact.id ?? `new-${i}`}>
        <div className="flex justify-between">
          <h4>{`Contact #${i + 1}`}</h4>
          <Button size="small" onClick={deleteContact}>
            {tr("Delete")}
          </Button>
        </div>
        <div className="flex justify-between">
          <Input
            style={{ width: "32%" }}
            label={tr("First Name")}
            required={!formik.values.contacts[i]?.first_name}
            formik={formik}
            name={`contacts.${i}.first_name`}
          />
          <Input
            style={{ width: "32%" }}
            label={tr("Last Name")}
            required={!formik.values.contacts[i]?.last_name}
            formik={formik}
            name={`contacts.${i}.last_name`}
          />
          <Input
            label={tr("Title")}
            style={{ width: "32%" }}
            formik={formik}
            name={`contacts.${i}.title`}
          />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <EmailInput
            label={tr("Email")}
            style={{ width: "49%" }}
            formik={formik}
            name={`contacts.${i}.email`}
          />
          <PhoneNumberInput
            label={tr("Phone")}
            style={{ width: "39%" }}
            formik={formik}
            name={`contacts.${i}.phone`}
          />
          <Input
            label="Ext"
            style={{ width: "9%" }}
            formik={formik}
            name={`contacts.${i}.extension`}
          />
        </div>
        <div className="flex flex-wrap gap-4 text-sm py-2">
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={!!formik.values.contacts?.[i]?.is_billing}
              onChange={(e) =>
                formik.setFieldValue(`contacts.${i}.is_billing`, e.target.checked)
              }
            />
            <span>{tr("Billing")}</span>
          </label>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={!!formik.values.contacts?.[i]?.is_notification}
              onChange={(e) =>
                formik.setFieldValue(
                  `contacts.${i}.is_notification`,
                  e.target.checked
                )
              }
            />
            <span>{tr("Notifications")}</span>
          </label>
        </div>
        <ContactFields
          formik={formik}
          basePath={`contacts.${i}.contact_info`}
          labels={{
            section: tr("Extra contact info"),
            addEmail: tr("Add extra email"),
            addPhone: tr("Add extra phone"),
            billing: tr("Billing"),
            notification: tr("Notification"),
          }}
        />
      </div>
    );
  });

  return (
    <div className="border rounded-md ml-3 mt-3 shadow-md relative">
      <div className="w-full flex justify-center">

        {/* Header */}
        <div className=" w-[270px]">
          <div className="fixed top-[63px] mx-4">
            <h2 className="border-b py-2">{isEditing ? tr("Edit Customer") : tr("New Customer")}</h2>

            <div className="flex justify-start gap-2 pt-4 pb-2">
              <Button
                onClick={() => history.goBack()}
              >
                {tr("Cancel")}
              </Button>
              {console.log({dirty: formik.dirty, isValid: formik.isValid, emailsValid: emailsValid})}
              <Button
                primary
                type="submit"
                isLoading={submitChanges.isLoading}
                disabled={!formik.dirty || !formik.isValid || !emailsValid}
                onClick={() => formik.handleSubmit()}
              >
                {tr("Save Changes")}
              </Button>
            </div>
          </div>
        </div>

      <div className="w-full flex justify-center border-l p-1">
        <form className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 px-4 py-6 divide-x [&>*]:px-4" onSubmit={formik.handleSubmit}>

          {/* Primary Contact */}
          <div>
            <h3>{tr("Primary Contact")}</h3>
            <div className="border-t border-black py-4 mt-4 mb-4">
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Input
                  label={tr("First Name")}
                  required={!formik.values.company_name}
                  formik={formik}
                  name="first_name"
                  style={{ width: "49%" }}
                />
                <Input
                  required={!formik.values.company_name}
                  formik={formik}
                  label={tr("Last Name")}
                  name="last_name"
                  style={{ width: "49%" }}
                />
              </div>
              <div className="flex justify-between">
                <Input
                  name="company_name"
                  formik={formik}
                  label={tr("Company Name")}
                  style={{ width: "49%" }}
                />
                <Input
                  name="title"
                  formik={formik}
                  label={tr("Title")}
                  style={{ width: "49%" }}
                />
              </div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <div className="relative group" style={{ width: "49%" }}>
                  <FavoriteStarButton
                    isPrimary={
                      (formik.values.defaultPrimaryEmailKey ?? "main") === "main"
                    }
                    onClick={() =>
                      formik.setFieldValue("defaultPrimaryEmailKey", "main")
                    }
                  />
                  <EmailInput
                    label={tr("Email")}
                    formik={formik}
                    name="email"
                    style={{ width: "100%" }}
                  />
                </div>
                <div className="relative group" style={{ width: "39%" }}>
                  <FavoriteStarButton
                    isPrimary={
                      (formik.values.defaultPrimaryPhoneKey ?? "main") === "main"
                    }
                    onClick={() =>
                      formik.setFieldValue("defaultPrimaryPhoneKey", "main")
                    }
                  />
                  <PhoneNumberInput
                    label={tr("Phone")}
                    formik={formik}
                    name="phone"
                    style={{ width: "100%" }}
                  />
                </div>
                <Input
                  label="Ext"
                  formik={formik}
                  name="extension"
                  style={{ width: "9%" }}
                />
              </div>
              <div className="flex flex-wrap gap-4 text-sm py-2">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!formik.values.is_billing}
                    onChange={(e) =>
                      formik.setFieldValue("is_billing", e.target.checked)
                    }
                  />
                  <span>{tr("Billing")}</span>
                </label>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!formik.values.is_notification}
                    onChange={(e) =>
                      formik.setFieldValue("is_notification", e.target.checked)
                    }
                  />
                  <span>{tr("Notifications")}</span>
                </label>
              </div>
              <div style={{ display: "flex", gap: "8px" }}>
                <Input
                  name="qb_name"
                  style={{ width: "100%" }}
                  formik={formik}
                  label={tr("QuickBooks Desktop Name")}
                  placeholder="Not available"
                  disabled={!isQbNameEditable} // Keeps it disabled until unlocked
                />
                {!isQbNameEditable && (
                  <IconButton onClick={() => setIsQbNameEditable(true)} title="Edit QuickBooks Name" className="mt-6">
                    ✏️
                  </IconButton>
                )}
              </div>

              {isQbNameEditable && (
                <div className="text-red-500 bg-red-100 p-2 rounded-md my-2">
                  ⚠ **WARNING:** Changing this value will break the link to previously synced records. To prevent creating a duplicate customer in QuickBooks, you must manually update the name in QuickBooks after making this change.
                </div>
              )}
              <ContactFields
                formik={formik}
                basePath="primary_contact_info"
                defaultPrimaryEmailKey={formik.values.defaultPrimaryEmailKey}
                defaultPrimaryPhoneKey={formik.values.defaultPrimaryPhoneKey}
                onSetDefaultPrimaryEmailKey={(key) =>
                  formik.setFieldValue("defaultPrimaryEmailKey", key)
                }
                onSetDefaultPrimaryPhoneKey={(key) =>
                  formik.setFieldValue("defaultPrimaryPhoneKey", key)
                }
                labels={{
                  section: tr("Additional emails and phones"),
                  addEmail: tr("Add extra email"),
                  addPhone: tr("Add extra phone"),
                  billing: tr("Billing"),
                  notification: tr("Notification"),
                }}
              />
            </div>
          </div>

          {/* Additional Contacts */}
          <div className="">
            <div className="border-b border-black pb-1 flex justify-between">
              <h3>{tr("Additional Contact")}</h3>
              <IconButton
                onClick={() =>
                  formik.setValues((v) => ({
                    ...v,
                    contacts: v.contacts
                      ? [...v.contacts, { ...contact, contact_info: [] }]
                      : [{ ...contact, contact_info: [] }],
                  }))
                }
              >
                +
              </IconButton>
            </div>
            {populateContacts}
          </div>

          <div>
            <h3 className="border-b border-black pb-4 mb-4">{tr("Defaults")}</h3>

            {/* Default email and phone for primary contact */}
            <Dropdown
              className="pb-2" 
              label={tr("Default email for primary contact")}
              value={formik.values.defaultPrimaryEmailKey ?? "main"}
              onChange={(ev) => {
                formik.setFieldValue("defaultPrimaryEmailKey", ev.target.value);
              }}
              options={primaryEmailSelectOptions}
            />
            <Dropdown
              className=""
              label={tr("Default phone for primary contact")}
              value={formik.values.defaultPrimaryPhoneKey ?? "main"}
              onChange={(ev) => {
                formik.setFieldValue("defaultPrimaryPhoneKey", ev.target.value);
              }}
              options={primaryPhoneSelectOptions}
            />

            {/* Address */}
            {isEditing ? (
              <Dropdown
                className="py-2"
                label={tr("Default billing address")}
                value={
                  formik.values.fk_billing_address_id != null
                    ? String(formik.values.fk_billing_address_id)
                    : ""
                }
                onChange={(ev) => {
                  const v = ev.target.value;
                  formik.setFieldValue(
                    "fk_billing_address_id",
                    v === "" ? null : Number(v)
                  );
                }}
                placeholderOption={{
                  value: "",
                  label: tr("No address on file"),
                }}
                options={(query.data?.info.addresses || []).map((a: TProperty) => ({
                  value: String(a.id),
                  label: a.full_address || a.address || `#${a.id}`,
                }))}
              />
            ) : (
              <div className="border-t border-black py-4 mt-4">
                <Input
                  name="name"
                  style={{ width: "100%" }}
                  formik={formik}
                  label={tr("Nickname")}
                />
                <Input
                  name="address"
                  style={{ width: "100%" }}
                  label={tr("Address")}
                  value={addressObj.address}
                  onChange={(e: any) => {
                    setAddressObj({
                      ...addressObj,
                      address: e.target.value,
                      isStandardizedAddress: false,
                    });
                  }}
                />
                <div className="flex justify-between">
                  <Input
                    name="city"
                    style={{ width: "40%" }}
                    label={tr("City")}
                    value={
                      addressObj.selectedOption
                        ? addressObj.selectedOption.city
                        : null
                    }
                    onChange={(e: any) => {
                      setAddressObj({
                        ...addressObj,
                        isStandardizedAddress: false,
                        selectedOption: {
                          ...addressObj.selectedOption,
                          city: e.target.value,
                        },
                      });
                    }}
                  />
                  <Input
                    name="state"
                    style={{ width: "20%" }}
                    formik={formik}
                    label={tr("State")}
                    maxLength={2}
                    value={
                      addressObj.selectedOption
                        ? addressObj.selectedOption.state
                        : null
                    }
                    onChange={(e: any) => {
                      setAddressObj({
                        ...addressObj,
                        isStandardizedAddress: false,
                        selectedOption: {
                          ...addressObj.selectedOption,
                          state: e.target.value,
                        },
                      });
                    }}
                  />
                  <Input
                    name="zipcode"
                    style={{ width: "30%" }}
                    formik={formik}
                    label={tr("Zipcode")}
                    value={
                      addressObj.selectedOption
                        ? addressObj.selectedOption.zipcode
                        : null
                    }
                    onChange={(e: any) => {
                      setAddressObj({
                        ...addressObj,
                        isStandardizedAddress: false,
                        selectedOption: {
                          ...addressObj.selectedOption,
                          zipcode: e.target.value,
                        },
                      });
                    }}
                  />
                </div>
              </div>
            )}

            {/* Tags */}
            <div>
              <h3>{tr("Tags")}</h3>
              <div className="border-t border-black py-4 mt-4 mb-4">
                {tagsQuery.isLoading ? (
                  <Spinner />
                ) : (
                  <>
                    {params.id === "new" ? (
                      <TagManager
                        customerId={params.id}
                        addedTags={formik.values.customer_tags}
                        availableTags={tagsQuery.data}
                        handleUpdateTag={handleUpdateTag}
                      />
                    ) : (
                      <TagManagement
                        customerId={params.id}
                        updateFormikTags={(updatedTags: any) =>
                          updateFormikTags(updatedTags)
                        }
                      />
                    )}
                  </>
                )}
              </div>
            </div>

          </div>
        </form>

        </div>
      </div>
    </div>
  );
}
